import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/hooks/useCompany';

export type OnboardingPersona = 'family' | 'company' | 'caregiver' | 'unknown';

export interface OnboardingStep {
  id: string;
  titleKey: string;
  descKey: string;
  icon: string; // lucide name string
  done: boolean;
  cta: string; // path or 'invite-modal'
  optional?: boolean;
}

export interface OnboardingState {
  persona: OnboardingPersona;
  steps: OnboardingStep[];
  completed: number;
  total: number;
  allDone: boolean;
  requiredDone: boolean;
  readyForFirstShift: boolean;
  firstPatientId: string | null;
  nextStep: OnboardingStep | null;
  /** true when the user already has real activity (shifts or executed tasks) */
  hasRealUsage: boolean;
}

function summarize(
  persona: OnboardingPersona,
  steps: OnboardingStep[],
  firstPatientId: string | null,
): OnboardingState {
  const required = steps.filter(s => !s.optional);
  const completed = steps.filter(s => s.done).length;
  const requiredDone = required.every(s => s.done);
  const nextStep = steps.find(s => !s.done && !s.optional) ?? steps.find(s => !s.done) ?? null;

  return {
    persona,
    steps,
    completed,
    total: steps.length,
    allDone: completed === steps.length,
    requiredDone,
    readyForFirstShift: requiredDone,
    firstPatientId,
    nextStep,
    hasRealUsage: false,
  };
}

export function useOnboardingProgress() {
  const { user } = useAuth();
  const { company, isCompanyMember, isLoading: companyLoading } = useCompany();

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-progress', user?.id, company?.companyId],
    enabled: !!user?.id && !companyLoading,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OnboardingState> => {
      if (!user?.id) {
        return summarize('unknown', [], null);
      }

      // Real usage signal: users who already started a shift or executed tasks
      // should never see the "getting started" instructions again.
      const [{ count: usageShifts }, { count: usageExecutions }] = await Promise.all([
        supabase.from('shifts').select('*', { count: 'exact', head: true }).eq('caregiver_id', user.id),
        supabase.from('task_executions').select('*', { count: 'exact', head: true }).eq('executed_by', user.id),
      ]);
      const hasRealUsage = (usageShifts ?? 0) > 0 || (usageExecutions ?? 0) > 0;

      const finalize = (
        persona: OnboardingPersona,
        steps: OnboardingStep[],
        patientId: string | null,
      ): OnboardingState => ({ ...summarize(persona, steps, patientId), hasRealUsage });

      // Fetch active patient links for the user (only active patients — archived/suspended are read-only)
      const { data: links } = await supabase
        .from('patient_user_links')
        .select('patient_id, role, shift_template_id, created_at, patients!inner(status)')
        .eq('user_id', user.id)
        .eq('active', true)
        .eq('patients.status', 'active')
        .order('created_at', { ascending: true });

      const ownerLinks = (links || []).filter(l => l.role === 'owner');
      const firstPatientId = ownerLinks[0]?.patient_id ?? links?.[0]?.patient_id ?? null;
      const isFamilyOwner = ownerLinks.length > 0;


      const persona: OnboardingPersona =
        isCompanyMember ? 'company' :
        isFamilyOwner ? 'family' :
        (links && links.length > 0) ? 'caregiver' :
        'unknown';

      // ---------- Company ----------
      if (persona === 'company' && company) {
        const [{ count: membersCount }, { data: assignments }] = await Promise.all([
          supabase.from('company_members').select('*', { count: 'exact', head: true })
            .eq('company_id', company.companyId).eq('is_active', true),
          supabase.from('company_patient_assignments')
            .select('patient_id, assigned_caregiver_id, patients!inner(status)')
            .eq('company_id', company.companyId).eq('is_active', true)
            .eq('patients.status', 'active'),
        ]);


        const patientIds = Array.from(new Set((assignments || []).map(a => a.patient_id)));
        const hasPatient = patientIds.length > 0;
        const hasAssignedCaregiver = (assignments || []).some(a => !!a.assigned_caregiver_id);

        let hasTemplate = false;
        let hasCarePlan = false;
        if (hasPatient) {
          const [{ data: tpls }, { data: tasks }] = await Promise.all([
            supabase.from('shift_templates').select('id')
              .in('patient_id', patientIds).is('deleted_at', null).limit(1),
            supabase.from('care_plan_tasks').select('id')
              .in('patient_id', patientIds).limit(1),
          ]);
          hasTemplate = (tpls?.length ?? 0) > 0;
          hasCarePlan = (tasks?.length ?? 0) > 0;
        }

        const firstCompanyPatientId = patientIds[0] ?? null;

        const steps: OnboardingStep[] = [
          { id: 'create-company', titleKey: 'onboarding.steps.companyCreated', descKey: 'onboarding.steps.companyCreatedDesc', icon: 'Building2', done: true, cta: '/app/company/settings' },
          { id: 'invite-team', titleKey: 'onboarding.steps.inviteTeam', descKey: 'onboarding.steps.inviteTeamDesc', icon: 'UserPlus', done: (membersCount ?? 0) > 1, cta: '/app/company?tab=team' },
          { id: 'link-patient', titleKey: 'onboarding.steps.linkPatient', descKey: 'onboarding.steps.linkPatientDesc', icon: 'Heart', done: hasPatient, cta: '/app/company?tab=team' },
          { id: 'shift-template', titleKey: 'onboarding.steps.shiftTemplate', descKey: 'onboarding.steps.shiftTemplateDesc', icon: 'Clock', done: hasTemplate, cta: firstCompanyPatientId ? `/app/patients/${firstCompanyPatientId}/templates/new` : '/app/company?tab=schedule' },
          { id: 'care-plan', titleKey: 'onboarding.steps.carePlan', descKey: 'onboarding.steps.carePlanDescCompany', icon: 'FileText', done: hasCarePlan, cta: firstCompanyPatientId ? `/app/patients/${firstCompanyPatientId}/care-plan` : '/app/company?tab=team' },
          { id: 'assign-caregiver', titleKey: 'onboarding.steps.assignCaregiver', descKey: 'onboarding.steps.assignCaregiverDesc', icon: 'CalendarCheck', done: hasAssignedCaregiver, cta: '/app/company?tab=team' },
        ];

        return finalize(persona, steps, firstCompanyPatientId ?? firstPatientId);
      }

      // ---------- Family owner ----------
      if (persona === 'family' && firstPatientId) {
        const [{ data: tpls }, { data: teamLinks }, { data: ranges }, { data: tasks }] = await Promise.all([
          supabase.from('shift_templates').select('id').eq('patient_id', firstPatientId).is('deleted_at', null).limit(1),
          supabase.from('patient_user_links').select('user_id, role, shift_template_id').eq('patient_id', firstPatientId).eq('active', true),
          supabase.from('patient_vital_ranges').select('id').eq('patient_id', firstPatientId).limit(1),
          supabase.from('care_plan_tasks').select('id').eq('patient_id', firstPatientId).limit(1),
        ]);

        const caregiverLinks = (teamLinks || []).filter(l => l.role === 'caregiver');
        const hasCaregiver = caregiverLinks.length > 0;
        const hasCaregiverOnShift = caregiverLinks.some(l => !!l.shift_template_id);
        const hasObserver = (teamLinks || []).some(l => l.role === 'observer');

        const steps: OnboardingStep[] = [
          { id: 'patient', titleKey: 'onboarding.steps.patientCreated', descKey: 'onboarding.steps.patientCreatedDesc', icon: 'Heart', done: true, cta: `/app/patients/${firstPatientId}` },
          { id: 'care-plan', titleKey: 'onboarding.steps.carePlan', descKey: 'onboarding.steps.carePlanDesc', icon: 'FileText', done: (tasks?.length ?? 0) > 0, cta: `/app/patients/${firstPatientId}/care-plan` },
          { id: 'shift-template', titleKey: 'onboarding.steps.shiftTemplate', descKey: 'onboarding.steps.shiftTemplateDescFamily', icon: 'Clock', done: (tpls?.length ?? 0) > 0, cta: `/app/patients/${firstPatientId}/templates/new` },
          { id: 'invite-caregiver', titleKey: 'onboarding.steps.inviteCaregiver', descKey: 'onboarding.steps.inviteCaregiverDesc', icon: 'UserPlus', done: hasCaregiver, cta: `/app/patients/${firstPatientId}` },
          { id: 'assign-caregiver', titleKey: 'onboarding.steps.assignCaregiverShift', descKey: 'onboarding.steps.assignCaregiverShiftDesc', icon: 'CalendarCheck', done: hasCaregiverOnShift, cta: `/app/patients/${firstPatientId}` },
          { id: 'vital-ranges', titleKey: 'onboarding.steps.vitalRanges', descKey: 'onboarding.steps.vitalRangesDesc', icon: 'HeartPulse', done: (ranges?.length ?? 0) > 0, cta: `/app/vital-ranges/${firstPatientId}`, optional: true },
          { id: 'invite-observer', titleKey: 'onboarding.steps.inviteObserver', descKey: 'onboarding.steps.inviteObserverDesc', icon: 'Eye', done: hasObserver, cta: `/app/patients/${firstPatientId}`, optional: true },
        ];

        return finalize(persona, steps, firstPatientId);
      }

      // ---------- Caregiver ----------
      if (persona === 'caregiver') {
        const [{ data: profile }, { count: pushCount }] = await Promise.all([
          supabase.from('profiles').select('full_name').eq('user_id', user.id).maybeSingle(),
          supabase.from('push_subscriptions').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
        ]);
        const shiftsCount = usageShifts;

        const steps: OnboardingStep[] = [
          { id: 'profile', titleKey: 'onboarding.steps.completeProfile', descKey: 'onboarding.steps.completeProfileDesc', icon: 'UserPlus', done: !!profile?.full_name?.trim(), cta: '/app/profile' },
          { id: 'push', titleKey: 'onboarding.steps.enablePush', descKey: 'onboarding.steps.enablePushDesc', icon: 'Bell', done: (pushCount ?? 0) > 0, cta: '/app/settings/notifications' },
          { id: 'first-shift', titleKey: 'onboarding.steps.firstShift', descKey: 'onboarding.steps.firstShiftDesc', icon: 'Clock', done: (shiftsCount ?? 0) > 0, cta: firstPatientId ? `/app/patients/${firstPatientId}/check-in` : '/app/patients' },
        ];

        return finalize(persona, steps, firstPatientId);
      }

      // No persona / unknown
      return {
        ...finalize(persona, [], firstPatientId),
        allDone: persona !== 'unknown',
        readyForFirstShift: false,
      };
    },
  });

  return {
    state: data,
    isLoading,
  };
}
